import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { COUNTIES_BY_USE } from '../lib/counties';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { CategoryGrid } from '../components/CategoryGrid';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Button, EmptyState, ErrorBlock, Field, LoadingBlock, Select, TextInput } from '../components/ui';
import '../styles/public.css';

/**
 * Find trusted services — the public directory (specification §5, §6).
 *
 * Everything here is readable without an account, and the API it reads runs
 * with no account context at all, so what arrives is what the public may see:
 * approved providers. This page does no filtering on status because it is never
 * given anything to filter.
 *
 * The phone number is shown, not hidden behind a form. §46 makes "public
 * provider phone numbers" a must-have, and the reason is worth stating: a
 * directory that makes you fill in a form to reach a plumber is a lead
 * generator selling your details, and this is not one.
 */

interface Category { id: string; slug: string; name: string; providers: number }
interface Provider {
  id: string; slug: string; business_name: string; description: string | null;
  contact_name: string | null; phone: string; whatsapp: string | null;
  email: string | null; county: string; town: string | null;
  is_featured: boolean; category_slug: string; category_name: string;
}

/** Kenyan numbers for wa.me: no plus, no spaces. */
const waLink = (n: string) => `https://wa.me/${n.replace(/[^0-9]/g, '')}`;

export function Services() {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');

  const category = params.get('category') ?? '';
  const county = params.get('county') ?? '';
  const q = params.get('q') ?? '';

  const cats = useApi<{ categories: Category[] }>('/api/v1/public/services/categories');
  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (category) p.set('category', category);
    if (county) p.set('county', county);
    if (q) p.set('q', q);
    return p.toString();
  }, [category, county, q]);
  const list = useApi<{ providers: Provider[]; total: number }>(
    `/api/v1/public/services${query ? `?${query}` : ''}`);

  /** Keeps the URL the source of truth, so a filtered view can be shared. */
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        <ExploreHero
          section="services"
          title="Find trusted services"
          sub="Plumbers, electricians, cleaners, movers and more, read by a person before they appear here."
        >
          <form
            className="xhero-search"
            onSubmit={(e) => { e.preventDefault(); setParam('q', search.trim()); }}
          >
            <div style={{ minWidth: 200 }}>
            <Field label="Service" htmlFor="svc-cat">
              <Select id="svc-cat" value={category} onChange={(e) => setParam('category', e.target.value)}>
                <option value="">All services</option>
                {(cats.data?.categories ?? []).map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}{c.providers ? ` (${c.providers})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ minWidth: 160 }}>
            <Field label="County" htmlFor="svc-county">
              {/* A list, not a text box. Typing was free to produce "Nrb",
                  "nairobi " and "Nairobi County", none of which match what a
                  provider chose from the same list on the way in. */}
              <Select id="svc-county" value={county}
                onChange={(e) => setParam('county', e.target.value)}>
                <option value="">Any county</option>
                {COUNTIES_BY_USE.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Search" htmlFor="svc-q">
              <TextInput id="svc-q" value={search} placeholder="Business name or what they do"
                onChange={(e) => setSearch(e.target.value)} />
            </Field>
          </div>
            <Button type="submit">Search</Button>
          </form>
        </ExploreHero>

        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
          <Link className="btn btn-primary" to="/services/register">List your business</Link>
        </div>

        {/* What this section covers, tappable. Hidden once a search is running:
            at that point the results are what the page is about, and a grid
            above them competes with the answer the visitor asked for. */}
        {!q && (
          <CategoryGrid
            section="services"
            categories={(cats.data?.categories ?? []).map(c => ({
              slug: c.slug, name: c.name, count: c.providers ?? 0,
            }))}
            active={category}
            onPick={(slug) => setParam('category', slug)}
          />
        )}

        {list.loading ? <LoadingBlock />
          : list.error ? <ErrorBlock error={list.error} onRetry={list.reload} />
          : (list.data?.providers.length ?? 0) === 0 ? (
            <EmptyState
              title="No businesses listed here yet"
              message={
                category || county || q
                  ? 'Try a different service or county.'
                  : 'Be the first. Listing your business is free.'
              }
            />
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 12 }}>
                {list.data!.total} business{list.data!.total === 1 ? '' : 'es'}
              </p>
              <div className="provider-grid">
                {list.data!.providers.map((p) => (
                  <article key={p.id} className="provider-card provider-card--services">
                    {p.is_featured && <span className="provider-featured">Featured</span>}
                    <div className="provider-head">
                      <span className="sect-tile sect-services provider-mono" aria-hidden>
                        {p.business_name.trim().charAt(0).toUpperCase()}
                      </span>
                      <h3><Link to={`/services/${p.slug}`}>{p.business_name}</Link></h3>
                    </div>
                    <p className="provider-meta">
                      {p.category_name} · {p.town ? `${p.town}, ` : ''}{p.county}
                    </p>
                    {p.description && (
                      <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)' }}>
                        {p.description.length > 140 ? `${p.description.slice(0, 140)}…` : p.description}
                      </p>
                    )}
                    <div className="provider-actions">
                      <a className="btn btn-secondary btn-sm" href={`tel:${p.phone}`}>Call</a>
                      {p.whatsapp && (
                        <a className="btn btn-ghost btn-sm" href={waLink(p.whatsapp)}
                          target="_blank" rel="noreferrer">WhatsApp</a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
      </main>
      <SiteFooter />
    </>
  );
}
